import React, { useState } from "react";
import { MarketingTick } from "../components/MarketingConsent";
import { useQuery } from "@tanstack/react-query";
import { Gift } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { safeNext } from "../lib/links";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { GoogleButton, OrRule } from "../components/GoogleSignIn";
import { api, errorMessage } from "../lib/api";
import { money } from "../lib/format";
import { ConnectBankButton } from "../components/cellar/ConnectBankButton";
import { HowItWorksStrip } from "../components/OptionExplainer";

const steps = ["Your account", "Connect your bank", "Choose a plan"];
const planAmounts = [2500, 5000, 10000, 20000];
const cadences = [
  { label: "Monthly", unit: "monthly", interval: 1 },
  { label: "Quarterly", unit: "monthly", interval: 3 },
];

export default function SignUpPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next")) || "/";
  const ref = (params.get("ref") || "").toUpperCase();
  const { data: invite } = useQuery<{ valid: boolean; referrer_first_name?: string; friend_pence?: number }>({ queryKey: ["referral-code", ref], queryFn: () => api.get(`/referrals/code/${ref}`).then((r) => r.data), enabled: !!ref });
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", password: "", marketing: false });
  const [busy, setBusy] = useState(false);
  const [mandateActive, setMandateActive] = useState(false);
  const [amount, setAmount] = useState(5000);
  const [cadence, setCadence] = useState(0);

  if (user && step === 0) return <Navigate to={next} replace />;

  const createAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name, ref, form.marketing);
      setStep(1);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const createPlan = async () => {
    setBusy(true);
    try {
      await api.post("/subscriptions", { amount_pence: amount, interval_unit: cadences[cadence].unit, interval: cadences[cadence].interval, day_of_month: 1 });
      toast.success("Your cellar plan is set up");
      navigate(next);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <div className="mx-auto max-w-5xl px-6 py-12 lg:px-10 lg:py-20" data-testid="signup-page">
        <img src="/brand/logo.png" alt="Pierre Hourlier Wines" className="h-24 w-auto" data-testid="auth-logo" />
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Open your cellar</h1>
        {ref && invite && !invite.valid && (
          <p className="mt-6 border border-border bg-[var(--parchment)] p-3 text-xs text-neutral-600" data-testid="signup-referral-invalid">That referral link isn't one we recognise ({ref}). You can still open your cellar — ask your friend to send their link again from Cellar &amp; Plan → Refer a friend.</p>
        )}
        {invite?.valid && (
          <div className="mt-6 flex items-start gap-3 border border-[var(--wine)] bg-[var(--parchment)] p-4 text-sm" data-testid="signup-referral-banner">
            <Gift className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wine)]" strokeWidth={1.5} />
            <span><strong>{invite.referrer_first_name} has invited you.</strong> Open your cellar with this link and {money(invite.friend_pence || 0)} lands in it as soon as your first payment clears — {invite.referrer_first_name} gets a thank-you too.</span>
          </div>
        )}
        <ol className="mt-10 grid grid-cols-3 gap-px border border-border" data-testid="signup-steps">
          {steps.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 p-4 text-sm ${i === step ? "bg-[var(--ink)] text-[var(--paper)]" : i < step ? "bg-[var(--parchment)]" : "text-neutral-500"}`} data-testid={`signup-step-${i}`}>
              <span className={`flex h-6 w-6 items-center justify-center border text-xs ${i === step ? "border-[var(--paper)]" : "border-border"}`}>{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </li>
          ))}
        </ol>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          {step === 0 && (
            <>
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl">Start with your details</h2>
                <p className="mt-3 text-sm text-neutral-600">You'll use these to sign in. Next we'll connect your bank by Direct Debit so you can build cellar credit — no card needed.</p>
                <p className="mt-6 text-sm text-neutral-600">Already a member? <Link to="/login" className="underline underline-offset-4" data-testid="signup-login-link">Sign in</Link></p>
                <div className="mt-10"><HowItWorksStrip /></div>
              </div>
              <form onSubmit={createAccount} className="space-y-6" data-testid="signup-form">
                <div className="space-y-2"><Label className="eyebrow">Full name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent" data-testid="signup-name-input" /></div>
                <div className="space-y-2"><Label className="eyebrow">Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-transparent" data-testid="signup-email-input" /></div>
                <div className="space-y-2"><Label className="eyebrow">Password (8+ characters)</Label><Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none bg-transparent" data-testid="signup-password-input" /></div>
                <MarketingTick checked={form.marketing} onChange={(v) => setForm({ ...form, marketing: v })} testId="signup-marketing-tick" />
                <Button type="submit" disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="signup-create-account-button">{busy ? "Creating…" : "Create account & continue"}</Button>
              </form>
              <OrRule />
              <GoogleButton next={next || "/"} ref={ref} label="Continue with Google — no password needed" className="py-4 hover-lift" testId="signup-google-button" />
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl">Connect your bank</h2>
                <p className="mt-3 text-sm text-neutral-600">A Direct Debit mandate through GoCardless lets you top up your cellar in one click and set a recurring plan. It opens right here — you won't leave this page.</p>
                <ul className="mt-6 space-y-2 text-sm text-neutral-600">
                  <li>· Sort code and account number, nothing else</li>
                  <li>· Nothing is collected until you choose to top up</li>
                  <li>· Cancel any time in your cellar or with your bank</li>
                </ul>
              </div>
              <div>
                <ConnectBankButton onActive={() => { setMandateActive(true); toast.success("Bank account connected"); }} />
                <div className="mt-6 flex items-center justify-between">
                  <button onClick={() => navigate(next)} className="text-sm text-neutral-500 underline underline-offset-4 hover:text-[var(--wine)]" data-testid="signup-skip-bank">Skip for now</button>
                  <Button onClick={() => setStep(2)} disabled={!mandateActive} variant="outline" className="rounded-none" data-testid="signup-next-plan">Choose a plan →</Button>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl">Build your cellar automatically</h2>
                <p className="mt-3 text-sm text-neutral-600">Pick an amount and how often. Each collection is credited to your cellar once your bank confirms it, and you draw it down bottle by bottle. Pause, skip or cancel whenever you like.</p>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="eyebrow">Amount</div>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {planAmounts.map((p) => (
                      <button key={p} onClick={() => setAmount(p)} className={`border py-3 text-sm tabular-nums transition-colors duration-200 ${amount === p ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`signup-plan-amount-${p}`}>{money(p)}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Cadence</div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {cadences.map((c, i) => (
                      <button key={c.label} onClick={() => setCadence(i)} className={`border py-3 text-sm transition-colors duration-200 ${cadence === i ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`signup-cadence-${c.interval}`}>{c.label}</button>
                    ))}
                  </div>
                </div>
                <Button onClick={createPlan} disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="signup-create-plan-button">{busy ? "Setting up…" : `Start ${money(amount)} ${cadences[cadence].label.toLowerCase()} plan`}</Button>
                <button onClick={() => navigate(next)} className="w-full text-sm text-neutral-500 underline underline-offset-4 hover:text-[var(--wine)]" data-testid="signup-skip-plan">No plan for now — I'll top up when I like</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
